import { useMemo, useState } from "react";
import { filterAndSortMcpServers } from "../lib/mcp-catalog";
import { MCP_REQUEST_URL, MCP_SLACK_LABEL, MCP_SLACK_URL, formatAddedDate } from "../lib/mcp-links";
import type { McpServer } from "../lib/mcp-registry";
import { onSpaLinkClick } from "../lib/link-click";
import { routeToHash, type McpSort } from "../lib/routing";
import { useMcpServers } from "../lib/use-mcp-servers";
import styles from "./McpServersPage.module.css";

interface Props {
  initialQuery?: string;
  initialSort?: McpSort;
  /** Keeps the URL in step with the search and sort, without adding history entries. */
  onFiltersChange: (query: string, sort: McpSort) => void;
  onSelectServer: (serverName: string) => void;
  /** Opens Help on the "Request a new MCP server" section. */
  onRequestHelp: () => void;
}

const SORTS: { id: McpSort; label: string }[] = [
  { id: "name", label: "Name" },
  { id: "recent", label: "Recently added" },
];

function isMcpSort(value: string): value is McpSort {
  return SORTS.some((s) => s.id === value);
}

function ServerCard({ server, onSelect }: { server: McpServer; onSelect: () => void }) {
  const added = formatAddedDate(server.publishedAt);
  return (
    <li className={styles.card}>
      <a
        href={routeToHash({ page: "mcpserver", serverName: server.serverName })}
        className={styles.cardLink}
        onClick={onSpaLinkClick(onSelect)}
      >
        <h2 className={styles.cardTitle}>{server.title}</h2>
      </a>
      <div className={styles.serverName}>{server.serverName}</div>
      {server.description && <p className={styles.description}>{server.description}</p>}
      <div className={styles.cardFooter}>{added && <span className={styles.added}>Added {added}</span>}</div>
    </li>
  );
}

/** The MCP Servers catalog (#939, Feature E): every platform server, the same for everyone. */
export default function McpServersPage({ initialQuery = "", initialSort = "name", onFiltersChange, onSelectServer, onRequestHelp }: Props) {
  const servers = useMcpServers();
  const [query, setQuery] = useState(initialQuery);
  const [sort, setSort] = useState<McpSort>(initialSort);

  const all = useMemo(() => (servers.status === "ready" ? servers.result.servers : []), [servers]);
  const filtered = useMemo(() => filterAndSortMcpServers(all, query, sort), [all, query, sort]);

  const updateQuery = (next: string) => {
    setQuery(next);
    onFiltersChange(next.trim(), sort);
  };
  const updateSort = (next: McpSort) => {
    setSort(next);
    onFiltersChange(query.trim(), next);
  };

  return (
    <div>
      <header className={styles.header}>
        <h1 className={styles.title}>MCP Servers</h1>
        <p className={styles.intro}>
          Every server on the CAI MCP Platform. Each one is a single HTTPS endpoint you reach with your Cox
          identity, using your own credentials sealed to you: no local install and no shared API keys. Connect
          from Claude Desktop, Cowork, Claude Code or any MCP client.
        </p>
      </header>

      {servers.status === "loading" && (
        <p role="status" className={styles.status}>Loading MCP servers…</p>
      )}

      {servers.status === "error" && (
        <div role="alert" className={styles.errorState}>
          <p className={styles.errorMessage}>
            Couldn&rsquo;t load the MCP server list. Try again, or ask in{" "}
            <a href={MCP_SLACK_URL} target="_blank" rel="noopener noreferrer" className={styles.link}>{MCP_SLACK_LABEL}</a>{" "}
            if it keeps happening.
          </p>
          <button type="button" onClick={servers.retry} className={styles.retryButton}>Try again</button>
        </div>
      )}

      {servers.status === "ready" && (
        <>
          <label className={styles.searchLabel}>
            <span className={styles.visuallyHidden}>Search MCP servers</span>
            <input
              type="search"
              value={query}
              onChange={(e) => updateQuery(e.target.value)}
              placeholder="Search by system or server name..."
              className={styles.searchInput}
            />
          </label>

          <div className={styles.toolbar}>
            <p className={styles.count} aria-live="polite">
              {all.length} servers available
              {query.trim() && <> &middot; showing {filtered.length}</>}
            </p>
            <label className={styles.sortRow}>
              <span className={styles.sortLabel}>Sort by:</span>
              <select
                value={sort}
                onChange={(e) => { if (isMcpSort(e.target.value)) updateSort(e.target.value); }}
                className={styles.sortSelect}
              >
                {SORTS.map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>

          {servers.result.source === "cached" && (
            <p className={styles.cachedNote}>
              The registry couldn&rsquo;t be reached, so this is the list from{" "}
              {formatAddedDate(servers.result.fetchedAt) || "your last visit"}. It may be missing new servers.
            </p>
          )}

          {filtered.length === 0 ? (
            <div className={styles.empty}>
              <p className={styles.emptyMessage}>
                No server matches. Ask in{" "}
                <a href={MCP_SLACK_URL} target="_blank" rel="noopener noreferrer" className={styles.emptyLink}>{MCP_SLACK_LABEL}</a>{" "}
                or{" "}
                <a href={MCP_REQUEST_URL} target="_blank" rel="noopener noreferrer" className={styles.emptyLink}>request a new server &rarr;</a>
              </p>
              <p className={styles.emptyNote}>
                The request form is for vendor MCP servers that already run somewhere. An admin reviews each one.{" "}
                <a
                  href={routeToHash({ page: "help", section: "mcp-request" })}
                  onClick={onSpaLinkClick(onRequestHelp)}
                  className={styles.noteLink}
                >
                  What it asks for &rarr;
                </a>
              </p>
              <button type="button" onClick={() => updateQuery("")} className={styles.clearButton}>Clear search</button>
            </div>
          ) : (
            <ul className={styles.grid} aria-label="MCP servers">
              {filtered.map((server) => (
                <ServerCard key={server.serverName} server={server} onSelect={() => onSelectServer(server.serverName)} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
